'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { Button } from '@/app/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/app/components/ui/sheet';
import { useDeleteElements } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { useElementSelector } from '../../hooks/use-element-selector';
import type { ElementsSelectorProps } from '../../types';
import { ElementSelectorTabs } from '../ElementSelectorTabs';
import { ElementList as SheetElementList } from './ElementList';

type SheetSelectorProps = ElementsSelectorProps & {
  onCreateOpen?: () => void;
};

/**
 * Sheet 模式的 Elements Selector (移动端底部弹出)
 */
export default function SheetSelector(props: SheetSelectorProps) {
  const { isOpen, onClose, onDelete, onCreateOpen } = props;
  const t = useTranslations();

  // 使用统一的业务逻辑 Hook
  const selector = useElementSelector(props);

  const { userInfo } = useStore((s) => ({ userInfo: s.userInfo }));

  // 仅当为自己的主体或 type 为 preset 时可删除
  const deletableSelected = useMemo(
    () =>
      selector.selectedElements.filter(
        (el) => el.type === 'preset' || (el.type === 'user' && el.creator_id === userInfo?.id),
      ),
    [selector.selectedElements, userInfo?.id],
  );

  const { handleDelete } = useDeleteElements({
    onSuccess: (deletedIds) => {
      onDelete?.(deletedIds);
      selector.setSelectedElements([]);
    },
  });

  const sheetProps = useMemo(
    () => ({
      open: isOpen,
      onOpenChange: (open: boolean) => {
        !open && onClose();
      },
    }),
    [isOpen, onClose],
  );

  if (!isOpen) return null;

  return (
    <Sheet {...sheetProps}>
      <SheetContent
        side="bottom"
        showClose={false}
        className="flex h-[66.6%] flex-col rounded-t-[20px] border-none bg-[#252B36] p-3"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
      >
        <SheetHeader className="relative h-8 flex-shrink-0 space-y-0">
          <SheetClose className="absolute top-0 bottom-0 left-0 h-8 px-3 text-xs leading-8">
            {t('elements.cancel')}
          </SheetClose>
          <SheetTitle className="text-center font-normal text-base leading-8">{t('elements.chooseElement')}</SheetTitle>
          <Button
            onClick={selector.handleDone}
            className="absolute top-0 right-0 bottom-0 m-0 h-8 bg-[#333A45] text-xs"
            size="default"
            disabled={!selector.selectedElements.length}
          >
            {t('elements.done')}
          </Button>
        </SheetHeader>

        <div className="flex-1 overflow-y-hidden">
          <ElementSelectorTabs {...selector} ElementListComponent={SheetElementList} onCreateOpen={onCreateOpen} />
        </div>

        <SheetFooter className="flex h-14 flex-shrink-0 items-center justify-start border-t-[1px] border-t-system-white12 text-sm sm:justify-start">
          <Button onClick={() => handleDelete(deletableSelected)} className="px-2" disabled={!deletableSelected.length}>
            <BaseLineDelete className="text-xl" />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
